import React, { useCallback } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';

// Components
import Stripe from '~/components/Stripe/Stripe';
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from '@mui/material';

// Assets
import { CheckedCIcon, UncheckedCIcon } from '~/assets/images';

const { TITLE, CLOSE_BUTTON, SUBMIT_BUTTON } = MESSAGES.ORDERS.CREATE.PAYMENT_METHOD_REQUIRED;

const FormLoader = () => (
  <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
    <CircularProgress sx={{ color: '#ed5c2f' }} />
  </Backdrop>
);

// Mirrors the review-step checkboxes (OrderFlow/ReviewOrder) so the confirmation looks the same.
const achConfirmationSx = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  m: '8px 0 0',
  p: '12px 14px',
  gap: '8px',
  borderRadius: '8px',
  border: '0.5px solid transparent',
  backgroundColor: '#f9fafb',
  transition: 'border 0.3s ease, background-color 0.3s ease',
  '&.checked': { border: '0.5px solid #ed5c2f', backgroundColor: '#ed5c2f0f' },
  '& .MuiCheckbox-root': { p: 0, mr: 0 },
  '& .MuiFormControlLabel-label': {
    color: '#575757',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
};

const AchFundsConfirmation = ({ checked, onChange }) => (
  <Box sx={{ mt: '16px' }}>
    <Typography
      sx={{
        color: '#111827',
        fontFamily: 'Inter',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 'normal',
      }}
    >
      {MESSAGES.ORDERS.CREATE.ACH_PAYMENT_HEADING}
    </Typography>
    <FormControlLabel
      className={checked ? 'checked' : ''}
      sx={achConfirmationSx}
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          icon={<img src={UncheckedCIcon} alt="unchecked" />}
          checkedIcon={<img src={CheckedCIcon} alt="checked" />}
        />
      }
      label={MESSAGES.ORDERS.CREATE.ACH_FUNDS_CONFIRMATION}
    />
  </Box>
);

/**
 * Blocks order placement until the account has a card on file.
 * Closing the modal (including after the card is saved) never submits the order,
 * so the user lands back on order confirmation with their order intact.
 * Only org admins can add a card, so everyone else gets an explanation instead of a form.
 * When `showAchConfirmation` is set (only for an admin impersonating a customer), the modal also offers the "customer pays via ACH" confirmation,
 * which greys out the card form and swaps the button to Confirm Order so that admin can place the order without a
 * card on file.
 */
const PaymentMethodRequiredModal = ({
  open,
  onClose,
  showAchConfirmation = false,
  achFundsConfirmed = false,
  onAchFundsConfirmedChange,
  onSubmitOrder,
}) => {
  const dispatch = useDispatch();
  const isOrgAdmin = useSelector((state) => state.userReducers.user?.isOrgAdmin) || false;

  const achConfirmation =
    showAchConfirmation && onAchFundsConfirmedChange ? (
      <AchFundsConfirmation checked={achFundsConfirmed} onChange={onAchFundsConfirmedChange} />
    ) : null;

  // With the ACH confirmation ticked there is no card to collect: grey out the card form and let the
  // admin confirm the order directly.
  const isAchSelected = !!achConfirmation && achFundsConfirmed && !!onSubmitOrder;

  const handleFormError = useCallback(() => {
    dispatch(failure(MESSAGES.ACCOUNT.WALLET.STRIPE.PAYMENT_METHOD_FORM_ERROR));
    onClose();
  }, [dispatch, onClose]);

  if (!isOrgAdmin) {
    return (
      <OlcFormModal
        open={open}
        onClose={onClose}
        title={TITLE}
        footer={
          isAchSelected ? (
            <>
              <OlcButton variant="secondary" onClick={onClose}>
                {CLOSE_BUTTON}
              </OlcButton>
              <OlcButton variant="primary" onClick={onSubmitOrder}>
                {SUBMIT_BUTTON}
              </OlcButton>
            </>
          ) : (
            <OlcButton variant="primary" onClick={onClose}>
              {CLOSE_BUTTON}
            </OlcButton>
          )
        }
      >
        <Typography>{MESSAGES.ACCOUNT.WALLET.STRIPE.UNAUTHORIZED_ADD_PAYMENT_METHOD}</Typography>
        {achConfirmation}
      </OlcFormModal>
    );
  }

  // Mounted only while open so a setup intent is created on demand, not on every page view.
  return open ? (
    <Stripe loadingFallback={<FormLoader />} onError={handleFormError}>
      <PaymentMethod
        open={open}
        handleClose={onClose}
        bottomContent={achConfirmation}
        disableForm={isAchSelected}
        submitAction={isAchSelected ? { label: SUBMIT_BUTTON, onClick: onSubmitOrder } : null}
      />
    </Stripe>
  ) : null;
};

export default PaymentMethodRequiredModal;
